import { storageGet, storageSet } from './storage';

/**
 * Common, clickable answers for the property-side job tabs. Most jobs reuse the
 * same PPE, access answers, equipment categories and review types — so we offer
 * them as quick-pick presets, seeded here and extended (persisted) as the
 * contractor adds their own.
 */
// Per-user browser storage (cw_property_presets__u<id>): custom answers are a
// convenience for the person typing them, not shared company data.
const KEY = 'property_presets';

// Seeds cover the trades a UK compliance / maintenance contractor works in, not
// just access and window cleaning: fire, emergency lighting, water hygiene,
// electrical, gas, lifting, roof safety, asbestos and general maintenance.
const SEED = {
  ppe: ['Hard hat', 'Full body harness', 'Fall-arrest lanyard', 'Cut-resistant gloves', 'Hi-vis vest', 'Safety boots', 'Eye protection', 'Ear defenders', 'Respirator / dust mask', 'FFP3 mask (asbestos)', 'Disposable coveralls', 'Insulated gloves (electrical)', 'Arc-flash face shield', 'Gas detector (personal)'],
  accessLabel: ['FM manned site?', 'Reception / security', 'Key location', 'Parking', 'Access route', 'Permit required', 'Site induction', 'Plant room access', 'Roof access', 'Riser access', 'Fire alarm panel location'],
  fmManned: ['Yes', 'No'],
  permit: ['Permit-to-work (roof)', 'Permit-to-work (confined space)', 'Hot works permit', 'Electrical isolation permit', 'Fire alarm isolation / disablement', 'Sprinkler impairment permit', 'Asbestos permit', 'Site induction required', 'None'],
  parking: ['On-site parking', 'Loading bay', 'Street parking (paid)', 'No parking — use public transport'],
  keyLocation: ['N/A — manned', 'Key safe on site', 'Collect from reception', 'Held by facilities manager'],
  aboutLabel: ['Property type', 'Floors', 'Size', 'Listed status', 'Category rating', 'Occupancy'],
  equipmentCategory: [
    'Fall arrest', 'Access equipment', 'Rope access', 'Restraint', 'BMU / cradle', 'Roof safety system',
    'Lift', 'Hoist', 'Lifting equipment (LOLER)', 'Platform',
    'Fire alarm', 'Emergency lighting', 'Fire extinguisher', 'Sprinkler', 'AOV / smoke vent', 'Fire door',
    'Water hygiene (L8)', 'Electrical (EICR)', 'Portable appliance (PAT)', 'Gas appliance',
    'Asbestos-containing material', 'General maintenance', 'Other',
  ],
  condition: ['Good', 'Attention', 'Defect'],
  reviewLabel: [
    'WAH review', 'LOLER thorough examination', 'Equipment logbook update', 'Rescue & emergency plan review',
    'Fire risk assessment', 'Fire alarm service (BS 5839-1)', 'Emergency lighting test (BS 5266)', 'Fire extinguisher service',
    'Sprinkler inspection', 'AOV / smoke vent test', 'Fire door inspection', 'Legionella risk assessment (L8)',
    'Water temperature monitoring', 'EICR (BS 7671)', 'PAT testing', 'Gas safety (CP12)', 'Asbestos re-inspection',
    'Staircase inspection', 'Flagpole inspection', 'Lightning protection test',
  ],
  qualification: [
    'IRATA Level 1', 'IRATA Level 2', 'IRATA Level 3', 'LOLER Competent Examiner', 'IPAF 3a/3b', 'PASMA',
    'Working at Height', 'Confined Space Entry', 'CSCS card', 'SSSTS', 'SMSTS', 'First Aid at Work',
    'BAFE SP203-1 (fire alarm)', 'FIA fire alarm training', 'BAFE SP101 (extinguishers)', 'Emergency lighting (ICEL)',
    'City & Guilds 18th Edition (BS 7671)', 'City & Guilds 2391 (inspection & testing)', 'NICEIC / NAPIT registered',
    'Gas Safe registered', 'Legionella control (L8 / HSG274)', 'Asbestos Awareness (Cat A)', 'UKATA non-licensable asbestos',
  ],
};

const readCustom = () => { const c = storageGet(KEY, {}); return c && typeof c === 'object' ? c : {}; };
const writeCustom = (c) => storageSet(KEY, c);

/** Merged preset list for a key: seeds + any custom the contractor has added. */
export function presetsFor(key) {
  const custom = readCustom()[key] || [];
  const base = SEED[key] || [];
  const out = [];
  const seen = new Set();
  [...base, ...custom].forEach((v) => { const s = String(v).trim(); if (s && !seen.has(s.toLowerCase())) { seen.add(s.toLowerCase()); out.push(s); } });
  return out;
}

/** Remember a custom answer so it's offered next time. */
export function savePreset(key, value) {
  const v = String(value || '').trim();
  if (!v) return presetsFor(key);
  const custom = readCustom();
  const list = custom[key] || [];
  if (![...(SEED[key] || []), ...list].some((x) => x.toLowerCase() === v.toLowerCase())) {
    custom[key] = [...list, v];
    writeCustom(custom);
  }
  return presetsFor(key);
}

/** Which value-preset key applies to a given access field label. */
export const accessValueKey = (label) => {
  const l = String(label).toLowerCase();
  if (l.includes('manned')) return 'fmManned';
  if (l.includes('permit')) return 'permit';
  if (l.includes('parking')) return 'parking';
  if (l.includes('key')) return 'keyLocation';
  return null;
};
